import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { useRef, useEffect, useState } from 'react';
import './Charts.css';

const legendMargin = {
  id : 'legendMargin',
  beforeInit: function(chart, legend, options) {
    const fitValue = chart.legend.fit;
    chart.legend.fit = function fit() {
      fitValue.bind(chart.legend)();
      return this.height += 16;
    };
  }
};

// 영역 하이라이트 플러그인
const areaHighlightPlugin = {
  id: 'areaHighlight',
  afterDraw: function(chart) {
    const { ctx, chartArea, scales } = chart;
    const { x } = scales;
    
    // hoveredIndex가 null이거나 undefined인 경우 하이라이트하지 않음
    if (chart._hoveredIndex === null || chart._hoveredIndex === undefined) return;
    
    const hoveredIndex = chart._hoveredIndex;
    const xScale = x;
    
    // 막대의 너비 계산
    const barWidth = xScale.width / xScale.ticks.length;
    const barX = xScale.getPixelForValue(hoveredIndex) - barWidth / 2;
    
    // 하이라이트 영역 그리기
    ctx.save();
    ctx.fillStyle = 'rgba(66, 133, 244, 0.1)';
    ctx.fillRect(barX, chartArea.top, barWidth, chartArea.bottom - chartArea.top);
    ctx.restore();
  }
};
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  legendMargin,
  areaHighlightPlugin
);

const BarChart = ({
  data,
  title = '',
  height = '300px',
  width = '100%',
  showLegend = true,
  showGrid = true,
  responsive = true,
  maintainAspectRatio = false,
  className = '',
  maxTicksLimit = 12,
  showDataLabels = false
}) => {
  const chartRef = useRef(null);
  const [cssColors, setCssColors] = useState({
    gray600: '#718096'
  });

  // CSS 변수에서 색상 값 가져오기
  useEffect(() => {
    const getCSSVariable = (variable) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    };

    setCssColors({
      gray600: getCSSVariable('--gray-600')
    });
  }, []);
  const chartData = {
    labels: data?.labels || [],
    datasets: data?.datasets || []
  };
  // x=0 위치의 그리드 라인 색상을 지정하는 함수
  const getXGridColor = (context) => {
    if (context.tick && context.tick.value === 0) {
      return cssColors.gray600; // x=0 위치의 선 색상
    }
    return 'transparent'; // 다른 그리드 라인은 투명
  };
  // y=0 위치의 그리드 라인 색상을 지정하는 함수
  const getYGridColor = (context) => {

    if (context.tick && context.tick.value === 0) {
      return cssColors.gray600; // y=0 위치의 선 색상
    }
    return 'transparent'; // 다른 그리드 라인은 투명
  };
  const options = {
    responsive,
    maintainAspectRatio,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          boxHeight: 5,
          boxWidth: 5,
          font: {
            size: 12,
            weight: '700'
          },
          color: cssColors.gray600,
        },
      },
      datalabels: {
        display: showDataLabels,
        anchor: 'end',
        align: 'top',
        color: cssColors.gray600,
        font: {
          size: 11,
          weight: '600'
        },
        formatter: (value) => {
          return value > 0 ? value : '';
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: getXGridColor,
          drawBorder: false
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          },
          maxTicksLimit: maxTicksLimit
        }
      },
      y: {
        display: true,
        beginAtZero: true,
        min: 0,
        grid: {
          display: true,
          color: getYGridColor,
          drawBorder: false
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          }
        }
      }
    },
    
    hover: {
      mode: 'index',
      intersect: false,
      animationDuration: 0
    },
    onHover: (event, activeElements, chart) => {
      if (activeElements.length > 0) {
        const dataIndex = activeElements[0].index;
        if (chart._hoveredIndex !== dataIndex) {
          chart._hoveredIndex = dataIndex;
          chart.update('none');
        }
      } else {
        if (chart._hoveredIndex !== null) {
          chart._hoveredIndex = null;
          chart.update('none');
        }
      }
    },
    elements: {
      bar: {
        borderRadius: 4
      }
    }
  };

  return (
    <div 
      className={`chart-wrapper bar-chart ${className}`}
      style={{ height, width }}
      onMouseLeave={() => {
        // 차트 영역을 벗어났을 때 하이라이트 제거
        if (chartRef.current) {
          chartRef.current._hoveredIndex = null;
          chartRef.current.update('none');
        }
      }}
    >
      <Bar 
        ref={chartRef}
        data={chartData} 
        options={options}
        plugins={showDataLabels ? [ChartDataLabels] : []}
      />
    </div>
  );
};

export default BarChart;
